import React, { useState } from 'react';
import { Input, InputNumber, ColorPicker, Tag } from 'antd';
import { useDispatch } from 'react-redux';

import { DevClusterAdmin, DevTrackAdmin, patchTrack } from 'state';
import { usePatchBuffer } from '../hooks';
import { Field, Section, LockedRow } from './inspectorBits';

type Props = {
  track: DevTrackAdmin;
  planCode: string;
  editable: boolean;
  clusters: DevClusterAdmin[];
  onSelectCluster: (id: number) => void;
};

const TrackInspector: React.FC<Props> = ({
  track,
  planCode,
  editable,
  clusters,
  onSelectCluster,
}) => {
  const dispatch = useDispatch();
  const [name, setName] = useState(track.name);
  const [blurb, setBlurb] = useState(track.blurb);
  const { push } = usePatchBuffer<{ name: string; blurb: string }>((p) =>
    dispatch(patchTrack({ id: track.id, plan: planCode, ...p }))
  );
  const patch = (p: Record<string, unknown>) =>
    dispatch(patchTrack({ id: track.id, plan: planCode, ...p }));

  return (
    <div className="devadmin-insp">
      <div className="devadmin-insp__head">
        <div>
          <div className="devadmin-insp__kind">Track</div>
          <div className="devadmin-insp__title">{name}</div>
        </div>
        <span className="devadmin-insp__swatch" style={{ background: track.color }} />
      </div>

      <Section title="Track">
        <Field
          label="Name"
          info="The track's name in the tree legend and its overview panel. Display only."
        >
          <Input
            value={name}
            disabled={!editable}
            onChange={(e) => {
              setName(e.target.value);
              push({ name: e.target.value });
            }}
          />
        </Field>
        <Field
          label="Blurb"
          info="A one-line description of the track, shown on the tree's overview panel. Display only."
        >
          <Input.TextArea
            value={blurb}
            disabled={!editable}
            autoSize={{ minRows: 2, maxRows: 4 }}
            onChange={(e) => {
              setBlurb(e.target.value);
              push({ blurb: e.target.value });
            }}
          />
        </Field>
        <Field
          label="Color"
          hint="node accent + edges"
          info="The track's accent color — tints every node in the track and the links between them."
        >
          <ColorPicker
            value={track.color}
            disabled={!editable}
            onChangeComplete={(c: any) =>
              patch({ color: typeof c === 'string' ? c : c?.toHexString?.() })
            }
          />
        </Field>
        <Field
          label="Order"
          info="Currently has no effect on the trainee view — track positions are fixed by the track code and the legend follows insertion order. Kept for future use."
        >
          <InputNumber
            value={track.order}
            disabled={!editable}
            onChange={(v) => v != null && patch({ order: v })}
          />
        </Field>
      </Section>

      <Section title="Clusters" hint="click to edit">
        <div className="devadmin-chiplist">
          {[...clusters]
            .sort((a, b) => a.order - b.order)
            .map((c) => (
              <Tag
                key={c.id}
                className="devadmin-clickchip"
                onClick={() => onSelectCluster(c.id)}
              >
                {c.code} · {c.name}
              </Tag>
            ))}
          {clusters.length === 0 && <div className="devadmin-muted">None.</div>}
        </div>
      </Section>

      <LockedRow
        label="Track code"
        value={track.code}
        why="The track's fixed identifier. It selects the hardcoded layout orientation (which way the track radiates from the core). Renaming it would break the layout, so new or renamed tracks are a v2 feature."
      />
    </div>
  );
};

export default TrackInspector;
